import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const PolicyManagement = () => {
  const theme = useThemeClasses();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    status: 'published'
  });

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/policies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPolicies(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      title: policy.title,
      slug: policy.slug,
      content: policy.content,
      status: policy.status
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (editingPolicy) {
        // Update existing policy
        await axios.put(
          `${API}/api/admin/policies/${editingPolicy.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Policy updated successfully');
      } else {
        // Create new policy
        await axios.post(
          `${API}/api/admin/policies`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Policy created successfully');
      }
      
      setIsEditing(false);
      setEditingPolicy(null);
      setFormData({ title: '', slug: '', content: '', status: 'published' });
      loadPolicies();
    } catch (error) {
      console.error('Failed to save policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/admin/policies/${policyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Policy deleted successfully');
      loadPolicies();
    } catch (error) {
      console.error('Failed to delete policy:', error);
      toast.error('Failed to delete policy');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingPolicy(null);
    setFormData({ title: '', slug: '', content: '', status: 'published' });
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>
            {editingPolicy ? 'Edit Policy' : 'Create New Policy'}
          </h2>
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            ← Back to List
          </button>
        </div>

        <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200 space-y-4`}>
          {/* Title */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
              Policy Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => {
                setFormData({ 
                  ...formData, 
                  title: e.target.value,
                  slug: generateSlug(e.target.value)
                });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Terms & Conditions"
            />
          </div>

          {/* Slug */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
              URL Slug
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., terms-and-conditions"
            />
            <p className={`text-xs mt-1 ${theme.textSecondary}`}>
              URL: /policies/{formData.slug}
            </p>
          </div>

          {/* Status */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          {/* Content */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
              Policy Content (HTML)
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={20}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="Enter HTML content..."
            />
            <p className={`text-xs mt-1 ${theme.textSecondary}`}>
              💡 Use HTML tags: &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, etc.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              {editingPolicy ? 'Update Policy' : 'Create Policy'}
            </button>
            <button
              onClick={handleCancel}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>Legal Pages & Policies</h2>
          <p className={theme.textSecondary}>
            Manage terms, privacy policy, and other legal documents
          </p>
        </div>
        <button
          onClick={() => setIsEditing(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Create New Policy
        </button>
      </div>

      <div className="grid gap-4">
        {policies.map((policy) => (
          <div 
            key={policy.id} 
            className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className={`text-xl font-semibold ${theme.textPrimary}`}>
                    {policy.title}
                  </h3>
                  <span 
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      policy.status === 'published' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {policy.status}
                  </span>
                </div>
                
                <p className={`text-sm ${theme.textSecondary} mb-2`}>
                  <strong>URL:</strong> /policies/{policy.slug}
                </p>
                
                <p className={`text-xs ${theme.textSecondary}`}>
                  Last updated: {new Date(policy.updated_at).toLocaleDateString()} • 
                  Version {policy.version}
                </p>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(policy)}
                  className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDelete(policy.id)}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  🗑️ Delete
                </button>
                <a
                  href={`/policies/${policy.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  👁️ View
                </a>
              </div>
            </div>
          </div>
        ))}

        {policies.length === 0 && (
          <div className={`${theme.card} p-12 text-center rounded-lg border border-gray-200`}>
            <div className="text-6xl mb-4">📄</div>
            <h3 className={`text-xl font-semibold mb-2 ${theme.textPrimary}`}>
              No policies yet
            </h3>
            <p className={`mb-4 ${theme.textSecondary}`}>
              Create your first legal policy to get started
            </p>
            <button
              onClick={() => setIsEditing(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Policy
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
